/**
 * Vehicle Contribution Instrument
 * Vehicle-specific document for title transfer
 * Requires current owner signature
 */

import { replaceMergeFields, getAllMergeFields } from './mergeFields';

export function generateVehicleInstrumentHtml(
  workflowData: any,
  unaData: any
): string {
  const mergeFields = getAllMergeFields(workflowData, unaData);
  
  const template = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    @page {
      size: Letter;
      margin: 0.75in;
      @bottom-center {
        content: "Page " counter(page) " of " counter(pages);
        font-family: Cambria, Georgia, 'Times New Roman', serif;
        font-size: 9pt;
        color: #666;
      }
    }
    body {
      font-family: Cambria, Georgia, 'Times New Roman', serif;
      font-size: 11pt;
      line-height: 1.85;
      color: #1a1a1a;
      max-width: 8.5in;
      margin: 0 auto;
      padding: 0;
    }
    h1 {
      text-align: center;
      font-size: 18pt;
      margin: 0 0 20pt 0;
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 0.12em;
      border-bottom: 2px solid #c9a961;
      padding-bottom: 14pt;
    }
    .section {
      margin-bottom: 16pt;
      text-align: justify;
    }
    .vehicle-details {
      border: 2px solid #c9a961;
      padding: 16pt;
      margin: 20pt 0;
      background-color: #fafafa;
      border-radius: 4pt;
    }
    .vehicle-details h3 {
      margin: 0 0 14pt 0;
      text-align: center;
      font-size: 12pt;
      font-weight: bold;
      font-style: italic;
      color: #333;
    }
    .detail-row {
      margin-bottom: 8pt;
    }
    .detail-label {
      font-weight: bold;
      display: inline-block;
      min-width: 1.5in;
    }
    .signature-section {
      margin-top: 40pt;
      page-break-inside: avoid;
    }
    .signature-block {
      margin-top: 24pt;
      padding: 20pt;
      border: 1px solid #ddd;
      border-radius: 4pt;
      background-color: #fafafa;
    }
    .signature-line {
      border-bottom: 2px solid #333;
      width: 3.5in;
      min-height: 0.6in;
      margin-bottom: 8pt;
      background-color: white;
    }
    .signature-label {
      font-size: 10pt;
      margin-top: 6pt;
      font-weight: 600;
    }
    .date-line {
      border-bottom: 1px solid #666;
      width: 2in;
      display: inline-block;
      margin-left: 8pt;
    }
    .dmv-note {
      margin-top: 30pt;
      padding: 14pt;
      background-color: #fffbeb;
      border-left: 4px solid #d97706;
      font-size: 10pt;
    }
  </style>
</head>
<body>
  <h1>Vehicle Contribution Instrument</h1>
  
  <div class="section">
    This Vehicle Contribution Instrument is executed on {{contribution_date}}, documenting 
    the private member contribution of a vehicle from {{contributor_name}} to {{receiving_entity}} 
    within the ecclesiastical jurisdiction of {{una_name}}.
  </div>
  
  <div class="vehicle-details">
    <h3>VEHICLE DESCRIPTION</h3>
    <div class="detail-row">
      <span class="detail-label">Year:</span> {{vehicle_year}}
    </div>
    <div class="detail-row">
      <span class="detail-label">Make:</span> {{vehicle_make}}
    </div>
    <div class="detail-row">
      <span class="detail-label">Model:</span> {{vehicle_model}}
    </div>
    <div class="detail-row">
      <span class="detail-label">Type:</span> {{vehicle_type}}
    </div>
    <div class="detail-row">
      <span class="detail-label">Color:</span> {{vehicle_color}}
    </div>
    <div class="detail-row">
      <span class="detail-label">VIN:</span> <strong>{{vehicle_vin}}</strong>
    </div>
  </div>
  
  <div class="section">
    <strong>Current Owner:</strong> {{contributor_name}}, {{contributor_address}}
  </div>
  
  <div class="section">
    <strong>New Owner:</strong> {{receiving_entity}}, an entity under the stewardship of 
    {{una_name}}, {{una_state}}
  </div>
  
  <div class="section">
    <strong>Purpose:</strong> {{vehicle_purpose}}
  </div>
  
  <div class="section">
    <strong>Nature of Transaction:</strong> This is a private member contribution made 
    within the ecclesiastical jurisdiction of {{una_name}}. It is not a sale, gift, or 
    donation under public law. The vehicle is being contributed as a private ecclesiastical 
    transaction to support the purposes of the UNA.
  </div>
  
  <div class="section">
    <strong>Representations:</strong> The current owner represents that they hold clear 
    title to the vehicle, free of liens and encumbrances (or that required lienholder 
    approval has been obtained), and have full authority to make this contribution.
  </div>
  
  <div class="section">
    <strong>Acceptance:</strong> {{receiving_entity}} accepts stewardship of this vehicle 
    and commits to maintaining and using it in accordance with the purposes of {{una_name}}.
  </div>
  
  <div class="signature-section">
    <div style="text-align: center; font-weight: bold; margin-bottom: 20pt; letter-spacing: 0.08em;">
      EXECUTED AS OF {{contribution_date}}
    </div>
    
    <div class="signature-block">
      <div style="font-weight: bold; margin-bottom: 12pt;">CURRENT OWNER (CONTRIBUTOR):</div>
      <div class="signature-line"></div>
      <div class="signature-label">{{contributor_name}}</div>
      <div class="signature-label">{{contributor_address}}</div>
      <div style="margin-top: 16pt;">
        Date: <span class="date-line"></span>
      </div>
    </div>
    
    <div class="signature-block">
      <div style="font-weight: bold; margin-bottom: 12pt;">NEW OWNER (RECEIVING ENTITY):</div>
      <div>{{receiving_entity}}</div>
      <div class="signature-line" data-signer-id="{{treasury_secretary_id}}" data-signature-role="primary_officer"></div>
      <div class="signature-label">By: {{treasury_secretary_name}}, {{primary_officer_role}}</div>
      <div class="signature-label" style="margin-left: 28pt;">On behalf of {{una_name}}</div>
      <div style="margin-top: 16pt;">
        Date: <span class="date-line"></span>
      </div>
    </div>
  </div>
  
  <div class="dmv-note">
    <strong>DMV Title Transfer Note:</strong> This document serves as internal ecclesiastical 
    record-keeping. Separate DMV forms and procedures will be required to complete the title 
    transfer with the state Department of Motor Vehicles. The receiving entity should present 
    this document along with the physical title and appropriate DMV forms to complete the 
    transfer.
  </div>
</body>
</html>
  `;
  
  return replaceMergeFields(template, mergeFields);
}
